import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/stores/toastStore'
import recruiterService from '@/services/recruiterService'
import { SubscriptionPlan } from '@/types'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { CheckCircle } from 'lucide-react'
import { Loader2 } from 'lucide-react'

export default function PacksPage() {
  const { t } = useTranslation('recruiter')
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchPlans()
  }, [])

  const fetchPlans = async () => {
    try {
      setLoading(true)
      const plansResponse = await recruiterService.getPacks()
      setPlans(plansResponse)
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.error || t('subscription.errorFetching')
          : t('subscription.errorFetching')
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async (planId: string) => {
    try {
      setSubscribing(planId)
      const { paymentUrl } = await recruiterService.createSubscriptionPayment(planId)
      window.location.href = paymentUrl
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.error || t('subscription.errorInitiatingPayment')
          : t('subscription.errorInitiatingPayment')
      toast.error(errorMessage)
      setSubscribing(null)
    }
  }

  const getDiscountedPrice = (pack: SubscriptionPlan): number | null => {
    if (!pack.discountPercent) return null
    return Math.round((pack.priceCents / 100) * (1 - pack.discountPercent / 100))
  }

  const formatOfferEndDate = (isoDate: string): string => {
    return new Date(isoDate).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  return (
    <div className="container-custom py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">
        {t('subscription.choosePlan')}
      </h1>
      <p className="text-lg text-gray-600 mb-6 text-center max-w-3xl mx-auto">
        {t('subscription.description')}
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {Array.isArray(plans) &&
          plans.map((plan) => {
            const discountedPrice = getDiscountedPrice(plan)
            const showLimitedTimeBadge = plan.isLimitedTimeOffer && !plan.isMostPopular
            const showLimitedTimeChip = plan.isLimitedTimeOffer && plan.isMostPopular

            return (
              <Card
                key={plan.id}
                className={`flex flex-col hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative ${
                  plan.isMostPopular
                    ? 'border-2 border-blue-600'
                    : plan.isLimitedTimeOffer
                      ? 'border-2 border-orange-500'
                      : ''
                }`}
              >
                {plan.isMostPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs font-bold uppercase px-3 py-1 rounded-full">
                    {t('subscription.mostPopular')}
                  </div>
                )}
                {showLimitedTimeBadge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs font-bold uppercase px-3 py-1 rounded-full">
                    {t('subscription.limitedTimeOffer')}
                  </div>
                )}
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-gray-900 text-center mb-2 mt-2">
                    {plan.name}
                  </h3>
                  {plan.description && (
                    <p className="text-gray-600 text-center mb-4 text-sm">{plan.description}</p>
                  )}

                  {/* Price block */}
                  <div className="text-center mb-2">
                    {discountedPrice !== null ? (
                      <>
                        <p className="text-2xl text-gray-400 line-through leading-none">
                          {(plan.priceCents / 100).toFixed(0)} TND
                        </p>
                        <p className="text-4xl font-bold text-gray-900">{discountedPrice} TND</p>
                      </>
                    ) : (
                      <p className="text-4xl font-bold text-gray-900">
                        {(plan.priceCents / 100).toFixed(0)} TND
                      </p>
                    )}
                  </div>

                  {/* Discount chip + limited-time chip */}
                  <div className="flex flex-wrap justify-center gap-2 mb-4">
                    {plan.discountPercent && (
                      <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {t('subscription.off', { percent: plan.discountPercent })}
                      </span>
                    )}
                    {showLimitedTimeChip && (
                      <span className="inline-block bg-orange-100 text-orange-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {t('subscription.limitedTimeOffer')}
                      </span>
                    )}
                  </div>

                  {/* Offer end date */}
                  {plan.limitedTimeOfferEndsAt && (
                    <p className="text-xs text-orange-500 text-center mb-4">
                      {t('subscription.offerEndsOn', {
                        date: formatOfferEndDate(plan.limitedTimeOfferEndsAt),
                      })}
                    </p>
                  )}

                  {/* Key Features */}
                  <div className="space-y-3 text-gray-700 mb-6">
                    <div className="flex items-center justify-between text-sm py-2 border-b border-gray-100">
                      <span className="text-gray-600">{t('subscription.aiMatches')}:</span>
                      <span className="font-semibold">
                        {plan.aiMatchesQuota >= 999
                          ? t('subscription.unlimited')
                          : plan.aiMatchesQuota}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm py-2 border-b border-gray-100">
                      <span className="text-gray-600">{t('subscription.contactReveals')}:</span>
                      <span className="font-semibold">
                        {plan.contactRevealsQuota >= 999
                          ? t('subscription.unlimited')
                          : plan.contactRevealsQuota}
                      </span>
                    </div>
                  </div>

                  {/* Additional Features */}
                  {Array.isArray(plan.features) && plan.features.length > 0 && (
                    <ul className="space-y-2 text-sm mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  variant={plan.isMostPopular ? 'primary' : 'secondary'}
                  className="w-full mt-auto flex items-center justify-center gap-2"
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={subscribing !== null}
                >
                  {subscribing === plan.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  {subscribing === plan.id
                    ? t('subscription.redirecting')
                    : t('subscription.selectPlan')}
                </Button>
              </Card>
            )
          })}
      </div>

      {/* Additional Info Section */}
      <div className="mt-16 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {t('subscription.howItWorks')}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="text-center">
            <div className="text-blue-600 text-4xl mb-2">🔍</div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('subscription.features.profileViews.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('subscription.features.profileViews.description')}
            </p>
          </Card>
          <Card className="text-center">
            <div className="text-blue-600 text-4xl mb-2">📧</div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('subscription.features.contactReveals.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('subscription.features.contactReveals.description')}
            </p>
          </Card>
          <Card className="text-center">
            <div className="text-blue-600 text-4xl mb-2">🤖</div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('subscription.features.aiMatching.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('subscription.features.aiMatching.description')}
            </p>
          </Card>
        </div>
      </div>
    </div>
  )
}
